import { useCallback, useEffect, useState } from 'react'
import { AdminAuthGuard } from '@/components/admin/AdminAuthGuard'
import { BoardPostDraftCard } from '@/components/admin/BoardPostDraftCard'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Loader2, RefreshCw } from 'lucide-react'
import {
  type BoardPostDraft,
  type BoardPostDraftDetectedTypeFilter,
  type BoardPostDraftMaxPostAgeDays,
  type BoardPostDraftStatus,
  boardDraftDetectedTypeLabel,
  fetchBoardPostDrafts,
} from '@/lib/boardPostDraftsApi'

const STATUS_TABS: readonly { readonly id: BoardPostDraftStatus; readonly label: string }[] = [
  { id: 'pending', label: 'Pending' },
  { id: 'published', label: 'Published' },
  { id: 'discarded', label: 'Discarded' },
]

const DETECTED_TYPE_FILTER_OPTIONS: readonly {
  readonly id: BoardPostDraftDetectedTypeFilter
  readonly label: string
}[] = [
  { id: 'all', label: 'All types' },
  { id: 'new_tattoo', label: boardDraftDetectedTypeLabel('new_tattoo') },
  { id: 'event_announcement', label: boardDraftDetectedTypeLabel('event_announcement') },
  { id: 'discount', label: boardDraftDetectedTypeLabel('discount') },
  { id: 'flash_drop', label: boardDraftDetectedTypeLabel('flash_drop') },
  { id: 'general', label: boardDraftDetectedTypeLabel('general') },
]

const POST_AGE_FILTER_OPTIONS: readonly {
  readonly id: string
  readonly label: string
  readonly maxPostAgeDays: BoardPostDraftMaxPostAgeDays
}[] = [
  { id: 'all', label: 'Any post date', maxPostAgeDays: null },
  { id: '7', label: 'Posted last 7 days', maxPostAgeDays: 7 },
  { id: '14', label: 'Posted last 14 days', maxPostAgeDays: 14 },
  { id: '30', label: 'Posted last 30 days', maxPostAgeDays: 30 },
]

function BoardPostDraftsAdminContent() {
  const [activeStatus, setActiveStatus] = useState<BoardPostDraftStatus>('pending')
  const [detectedTypeFilter, setDetectedTypeFilter] =
    useState<BoardPostDraftDetectedTypeFilter>('all')
  const [postAgeFilterId, setPostAgeFilterId] = useState('all')
  const [drafts, setDrafts] = useState<BoardPostDraft[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  const selectedPostAgeOption =
    POST_AGE_FILTER_OPTIONS.find((option) => option.id === postAgeFilterId) ??
    POST_AGE_FILTER_OPTIONS[0]

  const loadDrafts = useCallback(async () => {
    setIsLoading(true)
    setLoadError(null)
    try {
      const loadedDrafts = await fetchBoardPostDrafts({
        status: activeStatus,
        detectedType: detectedTypeFilter,
        maxPostAgeDays: selectedPostAgeOption.maxPostAgeDays,
        limit: 500,
      })
      setDrafts(loadedDrafts)
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Failed to load drafts')
      setDrafts([])
    } finally {
      setIsLoading(false)
    }
  }, [activeStatus, detectedTypeFilter, selectedPostAgeOption.maxPostAgeDays])

  useEffect(() => {
    void loadDrafts()
  }, [loadDrafts])

  const handleDraftUpdated = (updatedDraft: BoardPostDraft) => {
    if (updatedDraft.status === activeStatus) {
      setDrafts((previousDrafts) =>
        previousDrafts.map((draft) => (draft.id === updatedDraft.id ? updatedDraft : draft)),
      )
      return
    }
    setDrafts((previousDrafts) => previousDrafts.filter((draft) => draft.id !== updatedDraft.id))
  }

  const handleDraftRemoved = (draftId: string) => {
    setDrafts((previousDrafts) => previousDrafts.filter((draft) => draft.id !== draftId))
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-4xl px-4 py-6 md:px-6 md:py-8 space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold">Board post drafts</h1>
            <p className="text-sm text-muted-foreground">
              Review Instagram-sourced posts before publishing to the community board.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => void loadDrafts()} disabled={isLoading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-1">
          {STATUS_TABS.map((tab) => (
            <Button
              key={tab.id}
              variant={activeStatus === tab.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveStatus(tab.id)}
            >
              {tab.label}
            </Button>
          ))}
        </div>

        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="flex-1 space-y-1">
            <p className="text-xs text-muted-foreground">Post type</p>
            <Select
              value={detectedTypeFilter}
              onValueChange={(value) =>
                setDetectedTypeFilter(value as BoardPostDraftDetectedTypeFilter)
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DETECTED_TYPE_FILTER_OPTIONS.map((option) => (
                  <SelectItem key={option.id} value={option.id}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 space-y-1">
            <p className="text-xs text-muted-foreground">Source post date</p>
            <Select value={postAgeFilterId} onValueChange={setPostAgeFilterId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {POST_AGE_FILTER_OPTIONS.map((option) => (
                  <SelectItem key={option.id} value={option.id}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {!isLoading ? (
          <p className="text-sm text-muted-foreground">
            {drafts.length} draft{drafts.length === 1 ? '' : 's'}
            {postAgeFilterId !== 'all'
              ? ' (only drafts with a known Instagram post date)'
              : ''}
          </p>
        ) : null}

        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Loading drafts…
          </div>
        ) : null}

        {!isLoading && loadError ? (
          <p className="text-sm text-destructive">{loadError}</p>
        ) : null}

        {!isLoading && !loadError && drafts.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">
            No {activeStatus} drafts match these filters.
          </p>
        ) : null}

        {!isLoading && !loadError
          ? drafts.map((draft) => (
              <BoardPostDraftCard
                key={draft.id}
                draft={draft}
                onDraftUpdated={handleDraftUpdated}
                onDraftRemoved={handleDraftRemoved}
              />
            ))
          : null}
      </div>
    </div>
  )
}

export default function BoardPostDraftsAdminPage() {
  return (
    <AdminAuthGuard>
      <BoardPostDraftsAdminContent />
    </AdminAuthGuard>
  )
}
